import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { combineStyles, fonts, media, mixins } from 'consumer-components';

import AppDocumentTitle, { AppDocumentTitle as AppDocumentTitleTypes } from '../../AppDocumentTitle/AppDocumentTitle';
import ArrowBackButton from '../../ArrowBackButton/ArrowBackButton';
import Emblem from '../../../Emblem/Emblem';
import SignInButton from '../../SignInButton/SignInButton';
import SignOutButton from '../../SignOutButton/SignOutButton';
import * as deploymentConfig from '../../../../utils/deploymentConfig';
import { loginBackgroundDynamicColor } from '../../../../stylesheets/deploymentColors';
import { paneDefaultStyles } from '../paneStyles';
import * as userUtils from '../../../../utils/userUtils';
import * as featureFlags from '../../../../utils/featureFlags';

const topMarginMobileSmall = 60;
const topMarginMobileMedium = 48;
const topMinMarginMediumView = 120;

const styles = StyleSheet.create({
  leftPane: combineStyles(mixins.padding(16, '4%', 20), {
    [media.mediumView]: combineStyles(mixins.padding('5%', '10%'), {
      height: '100vh',
    }),
  }),
  header: {
    display: 'flex',
    alignItems: 'center',
  },
  emblemWrapper: {
    // this width: 100% specification ensures that SVGs are sized appropriately; otherwise, they don't appear at all
    width: '100%',
    paddingRight: 32,
  },
  buttonWrapper: {
    alignItems: 'center',
    display: 'flex',
    marginLeft: 'auto',
  },
  textContainer: {
    width: '92%,',
    [media.mediumView]: {
      width: '80%',
      pointerEvents: 'none',
      position: 'absolute',
      top: 0,
    },
  },
  // we want a min-margin of 120px and a max-margin of 25vh
  // the min-margin is enforced as the margin on the contentContainer and
  // marginTop is a variable size container up to (max-margin - min-margin)
  //
  // Because this is a margin relative to the top of the page, not the buttom of
  // the header, these margins necessarily overlap the header, so their containing
  // element (with `styles.textContainer`) has `pointerEvents: 'none'` so you can
  // still click on buttons in the header.
  //
  // Any children that need to be clickable should have `pointerEvents: 'auto'`
  marginTop: {
    [media.mediumView]: {
      height: `calc(25vh - ${topMinMarginMediumView}px)`,
    },
  },
  backButtonWrapper: {
    ...fonts.Roboto,
    color: loginBackgroundDynamicColor,
    fontSize: 14,
    height: 20,
    position: 'absolute',
    top: -45,
  },
  titleMargin: {
    position: 'relative',
    marginTop: topMarginMobileSmall,
    [media.mobileMedium]: {
      marginTop: topMarginMobileMedium,
    },
    [media.mediumView]: {
      marginTop: topMinMarginMediumView,
    },
  },
  subtitle: {
    ...fonts.Roboto,
    color: loginBackgroundDynamicColor,
    pointerEvents: 'auto',
    fontSize: 14,
    lineHeight: '1.4em',
    paddingTop: 8,
    [media.mediumView]: {
      paddingTop: 15,
    },
  },
  footer: combineStyles(mixins.padding(16, 20, 20), {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    [media.mediumView]: mixins.padding(20, '10%'),
  }),
});

type LoginButton = 'Sign up' | 'Sign in' | undefined | null;

export interface LeftPaneProps {
  appDocumentTitleProps: AppDocumentTitleTypes.Props;
  title?: string;
  showBackButton?: boolean;
  loginButton?: LoginButton;
  subtitle?: string | React.ReactFragment;
  footer?: React.ReactFragment;
  renderBody?: () => React.ReactFragment;
}

export default class LeftPane extends React.Component<LeftPaneProps> {
  _renderBody = () => {
    const { title, subtitle, renderBody } = this.props;
    if (renderBody) return renderBody();
    if (!title) throw new Error(`If renderBody isn't provided, you must provide a title.`);

    return (
      <div>
        <h1 className={css(paneDefaultStyles.title)} data-it="LeftPane-title">
          {title}
        </h1>
        {subtitle && (
          <h2 className={css(styles.subtitle)} data-it="LeftPane-subtitle">
            {subtitle}
          </h2>
        )}
      </div>
    );
  };

  render() {
    const { appDocumentTitleProps, footer, showBackButton, loginButton: loginButtonProp } = this.props;

    let loginButton: LoginButton = loginButtonProp;

    const disableSignUp =
      deploymentConfig.get().disableBorrowerBlendLogin ||
      (!deploymentConfig.get().allowSignup && loginButtonProp === 'Sign up');
    const userLoggedIn =
      featureFlags.isEnabled('applicationChooserInDropdownMenu20190109') && userUtils.getCurrentUser();
    const showLogoutButton = userLoggedIn && featureFlags.isEnabled('signoutButtonOnMoreLoginPages20190912');

    if (disableSignUp || userLoggedIn) {
      loginButton = null;
    }

    return (
      <AppDocumentTitle {...appDocumentTitleProps}>
        <div className={css(styles.leftPane)}>
          <div className={css(styles.header)}>
            <div className={css(styles.emblemWrapper)}>
              <Emblem />
            </div>
            {loginButton && (
              <div className={css(styles.buttonWrapper)}>
                <SignInButton buttonText={loginButton} />
              </div>
            )}
            {showLogoutButton && (
              <div className={css(styles.buttonWrapper)}>
                <SignOutButton buttonText="Sign Out" />
              </div>
            )}
          </div>
          <div className={css(styles.textContainer)}>
            <div className={css(styles.marginTop)} />
            <div className={css(styles.titleMargin)}>
              {showBackButton && (
                <div className={css(styles.backButtonWrapper)} data-it="LeftPane-backButton">
                  <ArrowBackButton />
                </div>
              )}
              {this._renderBody()}
            </div>
          </div>

          {footer && (
            <div className={css(styles.footer)} data-it="LeftPane-footer">
              {footer}
            </div>
          )}
        </div>
      </AppDocumentTitle>
    );
  }
}
